import type { FuelType, PriceInfo } from '@/types';
import { formatPrice, formatRelativeTime } from '@/utils/format';
import { FUEL_TYPE_SHORT_LABELS, FUEL_TYPE_COLORS } from '@/constants';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';

interface PriceCardProps {
  fuelType: FuelType;
  priceInfo: PriceInfo | null;
  className?: string;
  isPrimary?: boolean;
  rank?: number;
  averagePrice?: number | null;
}

export function PriceCard({
  fuelType,
  priceInfo,
  className,
  isPrimary = false,
  rank,
  averagePrice,
}: PriceCardProps) {
  const color = FUEL_TYPE_COLORS[fuelType];
  const label = FUEL_TYPE_SHORT_LABELS[fuelType];

  if (!priceInfo) {
    return (
      <div
        className={clsx(
          'p-3 rounded-xl border border-gray-200 bg-gray-50',
          className
        )}
      >
        <div className="flex items-center gap-2 mb-1">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-lg font-bold text-gray-400">Not available</div>
      </div>
    );
  }

  const isCheapest = rank === 1;
  const priceDiff = averagePrice != null ? priceInfo.price - averagePrice : null;
  const isAboveAverage = priceDiff != null && priceDiff > 0;
  const isBelowAverage = priceDiff != null && priceDiff < 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={clsx(
        'relative p-4 rounded-xl border-2 transition-shadow',
        isPrimary && 'shadow-md',
        isCheapest && 'ring-2 ring-green-500 ring-offset-2',
        className
      )}
      style={{
        borderColor: color,
        backgroundColor: `${color}10`,
      }}
    >
      {/* Cheapest badge */}
      {isCheapest && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
          Cheapest
        </div>
      )}

      {/* Rank badge (if not cheapest but in top 3) */}
      {rank && rank > 1 && rank <= 3 && (
        <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
          {rank === 2 ? '2nd' : '3rd'} cheapest
        </div>
      )}

      {/* Fuel type label with colour dot */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-sm font-medium" style={{ color }}>
          {label}
        </span>
        {isPrimary && (
          <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
            Primary
          </span>
        )}
      </div>

      {/* Price */}
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-bold text-gray-900">
          {formatPrice(priceInfo.price)}
        </span>
        <span className="text-sm text-gray-500">per litre</span>
      </div>

      {/* Price comparison */}
      {priceDiff != null && Math.abs(priceDiff) >= 0.1 && (
        <div
          className={clsx(
            'mt-2 text-sm font-medium flex items-center gap-1',
            isBelowAverage && 'text-green-600',
            isAboveAverage && 'text-red-600'
          )}
        >
          {isBelowAverage ? (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span>{Math.abs(priceDiff).toFixed(1)}p below average</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span>{Math.abs(priceDiff).toFixed(1)}p above average</span>
            </>
          )}
        </div>
      )}

      {/* Last updated */}
      <div className="mt-2 text-xs text-gray-500">
        {formatRelativeTime(priceInfo.updated)}
      </div>
    </motion.div>
  );
}
